import React, { useState, useEffect } from "react";
import "./styles.css";
import ImageZoomModal from "../../../components/ImageZoomModal";
import {
  Form,
  Input,
  Select,
  Row,
  Col,
  InputNumber,
  DatePicker,
  Button,
  notification,
} from "antd";
import { db, storage } from "../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import { ref, listAll, getDownloadURL } from "firebase/storage";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { ArrowLeftOutlined } from "@ant-design/icons";
import ConditionalField from "../../../components/ConditionalField";

const { Item, useForm } = Form;

function ValvulaControleForm({ valvula }) {
  const [form] = useForm();
  const [clientes, setClientes] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [imagesValvula, setImagesValvula] = useState([]);
  const [imagesPlaqueta, setImagesPlaqueta] = useState([]);
  const [zoomModal, setZoomModal] = useState({ visible: false, images: [], index: 0, title: "" });
  const navigate = useNavigate();

  const getUnidades = (id, listaClientes) => {
    const resultado = (listaClientes || clientes).find(
      (cliente) => cliente.id === id
    );
    setUnidades(resultado?.unidades || []);
  };

  // Busca clientes
  useEffect(() => {
    const fetchClientesFirebase = async () => {
      try {
        const snapshot = await getDocs(collection(db, "clientes"));
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        setClientes(data);

        // se já veio uma válvula, atualiza unidades dela
        if (valvula?.cliente_id) {
          getUnidades(valvula.cliente_id, data);
        }
      } catch (error) {
        console.error("Erro ao buscar clientes:", error);
        notification.error({
          message: "Erro ao carregar clientes",
          description: "Tente novamente mais tarde.",
        });
      }
    };

    fetchClientesFirebase();
  }, [valvula]);

  // Busca imagens
  async function getFiles() {
    if (!valvula?.id) return;

    try {
      const listValvulaRef = ref(storage, `${valvula.id}/valvula`);
      const listPlaquetaRef = ref(storage, `${valvula.id}/plaqueta`);

      const [resultadoValvula, resultadoPlaqueta] = await Promise.all([
        listAll(listValvulaRef),
        listAll(listPlaquetaRef),
      ]);

      const imagesValvula = await Promise.all(
        resultadoValvula.items.map((image) => getDownloadURL(image))
      );
      const imagesPlaqueta = await Promise.all(
        resultadoPlaqueta.items.map((image) => getDownloadURL(image))
      );

      setImagesValvula(imagesValvula);
      setImagesPlaqueta(imagesPlaqueta);
    } catch (error) {
      console.error("Erro ao carregar imagens:", error);
    }
  }

  // Preenche o form quando a válvula chega
  useEffect(() => {
    if (!valvula) return;

    const dataCalibracao = valvula.dataCalibracao
      ? valvula.dataCalibracao.toDate
        ? dayjs(valvula.dataCalibracao.toDate())
        : dayjs(valvula.dataCalibracao)
      : null;

    const proximaCalibracao = valvula.proximaCalibracao
      ? valvula.proximaCalibracao.toDate
        ? dayjs(valvula.proximaCalibracao.toDate())
        : dayjs(valvula.proximaCalibracao)
      : null;

    form.setFieldsValue({
      ...valvula,
      dataCalibracao,
      proximaCalibracao,
    });

    getFiles();
  }, [valvula, form]);

  return (
    <>
      <Form
        layout="vertical"
        form={form}
        disabled // 🔒 tudo somente leitura para o cliente
        initialValues={{
          unidadeInlet: "pol",
          unidadeOutlet: "pol",
          unidadeNormaFlange: "ASME",
        }}
      >
        <h2 className="tituloValvula">VÁLVULA CONTROLE</h2>

        <ConditionalField value={valvula?.cliente_id}>
          <Row gutter={[12]}>
            <Col span={12}>
              <Item label="Cliente" name="cliente_id">
                <Select
                  placeholder="Selecione um cliente"
                  style={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    minWidth: "250px",
                  }}
                >
                  {clientes.map((cliente) => (
                    <Select.Option key={cliente.id} value={cliente.id}>
                      {cliente.nomeEmpresa}
                    </Select.Option>
                  ))}
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.unidadeCliente}>
          <Row gutter={[12]}>
            <Col span={12}>
              <Item label="Unidade" name="unidadeCliente">
                <Select
                  style={{
                    maxHeight: "150px",
                    overflowY: "auto",
                    minWidth: "250px",
                  }}
                >
                  <Select.Option value="">Selecione uma unidade</Select.Option>
                  {unidades.map((unidade, index) => (
                    <Select.Option key={index} value={unidade.unidade}>
                      {unidade.unidade}
                    </Select.Option>
                  ))}
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.tipo}>
          <Row gutter={[12]}>
            <Col span={12}>
              <Item label="Tipo" name="tipo">
                <Select>
                  <Select.Option value="C-VGL">C-VGL</Select.Option>
                  <Select.Option value="C-VES">C-VES</Select.Option>
                  <Select.Option value="C-VBO">C-VBO</Select.Option>
                  <Select.Option value="C-REGULADORA DE PRESSÃO">
                    C-REGULADORA DE PRESSÃO
                  </Select.Option>
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.tag}>
          <Item label="Tag" name="tag">
            <Input placeholder="Tag" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.fabricante}>
          <Item label="Fabricante:" name="fabricante">
            <Input placeholder="" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.modelo}>
          <Item label="Modelo:" name="modelo">
            <Input placeholder="" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.numeroSerie}>
          <Item label="Número de Série:" name="numeroSerie">
            <Input placeholder="" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.diametroInlet || valvula?.classePressaoInlet}>
          <Row gutter={[8]}>
            <Col span={8}>
              <Item label="Diâmetro Inlet:" name="diametroInlet">
                <InputNumber style={{ width: "100%" }} />
              </Item>
            </Col>
            <Col span={4}>
              <Item label="Unid." name="unidadeInlet">
                <Select>
                  <Select.Option value="pol">pol</Select.Option>
                  <Select.Option value="mm">mm</Select.Option>
                </Select>
              </Item>
            </Col>
            <Col span={8}>
              <Item label="Classe de Pressão Inlet:" name="classePressaoInlet">
                <Select>
                  <Select.Option value="125#">125#</Select.Option>
                  <Select.Option value="150#">150#</Select.Option>
                  <Select.Option value="300#">300#</Select.Option>
                  <Select.Option value="600#">600#</Select.Option>
                  <Select.Option value="900#">900#</Select.Option>
                  <Select.Option value="1,500#">1,500#</Select.Option>
                  <Select.Option value="2,500#">2,500#</Select.Option>
                  <Select.Option value="3,000#">3,000#</Select.Option>
                  <Select.Option value="5,000#">5,000#</Select.Option>
                  <Select.Option value="10,000#">10,000#</Select.Option>
                  <Select.Option value="15,000#">15,000#</Select.Option>
                  <Select.Option value="20,000#">20,000#</Select.Option>
                  <Select.Option value="5K">5K</Select.Option>
                  <Select.Option value="10K">10K</Select.Option>
                  <Select.Option value="16K">16K</Select.Option>
                  <Select.Option value="20K">20K</Select.Option>
                  <Select.Option value="30K">30K</Select.Option>
                  <Select.Option value="40K">40K</Select.Option>
                  <Select.Option value="50K">50K</Select.Option>
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.diametroOutlet || valvula?.classePressaoOutlet}>
          <Row gutter={[8]}>
            <Col span={8}>
              <Item label="Diâmetro Outlet:" name="diametroOutlet">
                <InputNumber style={{ width: "100%" }} />
              </Item>
            </Col>
            <Col span={4}>
              <Item label="Unid." name="unidadeOutlet">
                <Select>
                  <Select.Option value="pol">pol</Select.Option>
                  <Select.Option value="mm">mm</Select.Option>
                </Select>
              </Item>
            </Col>
            <Col span={8}>
              <Item label="Classe de Pressão Outlet:" name="classePressaoOutlet">
                <Select>
                  <Select.Option value="125#">125#</Select.Option>
                  <Select.Option value="150#">150#</Select.Option>
                  <Select.Option value="300#">300#</Select.Option>
                  <Select.Option value="600#">600#</Select.Option>
                  <Select.Option value="900#">900#</Select.Option>
                  <Select.Option value="1,500#">1,500#</Select.Option>
                  <Select.Option value="2,500#">2,500#</Select.Option>
                  <Select.Option value="3,000#">3,000#</Select.Option>
                  <Select.Option value="5,000#">5,000#</Select.Option>
                  <Select.Option value="10,000#">10,000#</Select.Option>
                  <Select.Option value="15,000#">15,000#</Select.Option>
                  <Select.Option value="20,000#">20,000#</Select.Option>
                  <Select.Option value="5K">5K</Select.Option>
                  <Select.Option value="10K">10K</Select.Option>
                  <Select.Option value="16K">16K</Select.Option>
                  <Select.Option value="20K">20K</Select.Option>
                  <Select.Option value="30K">30K</Select.Option>
                  <Select.Option value="40K">40K</Select.Option>
                  <Select.Option value="50K">50K</Select.Option>
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.normaFlange}>
          <Row gutter={[8]}>
            <Col span={8}>
              <Item label="Norma de Flange:" name="normaFlange">
                <Input list="normaFlange" />
              </Item>
            </Col>
            <Col span={4}>
              <Item label="Selecione:" name="unidadeNormaFlange">
                <Select>
                  <Select.Option value="ASME">ASME</Select.Option>
                  <Select.Option value="DIN">DIN</Select.Option>
                  <Select.Option value="JIS">JIS</Select.Option>
                </Select>
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.materialCorpo}>
          <Item label="Material do Corpo:" name="materialCorpo">
            <Input list="materialCorpo" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.materialSede}>
          <Item label="Material da Sede:" name="materialSede">
            <Input list="materialSede" />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.face}>
          <Col span={8}>
            <Item label="Face a face:" name="face">
              <InputNumber style={{ width: "100%" }} suffix="mm" />
            </Item>
          </Col>
        </ConditionalField>

        <ConditionalField value={valvula?.tipoCastelo}>
          <Item label="Tipo de Castelo:" name="tipoCastelo">
            <Input />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.acionamento}>
          <Item label="Acionamento:" name="acionamento">
            <Input />
          </Item>
        </ConditionalField>

        <ConditionalField value={valvula?.dataCalibracao || valvula?.proximaCalibracao}>
          <Row gutter={[8]}>
            <Col span={8}>
              <Item label="Data de Calibração:" name="dataCalibracao">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Item>
            </Col>
            <Col span={8}>
              <Item label="Próxima Calibração:" name="proximaCalibracao">
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
              </Item>
            </Col>
          </Row>
        </ConditionalField>

        <ConditionalField value={valvula?.observacoes}>
          <Item label="Observações:" name="observacoes">
            <Input.TextArea rows={4} />
          </Item>
        </ConditionalField>

        <ConditionalField value={imagesValvula.length > 0 ? imagesValvula : null}>
          <div className="form-group">
            <label className="titulo-upload">Fotos da Válvula:</label>
            <div className="images-container">
              <div className="images-display">
                {imagesValvula.map((item, i) => (
                  <img
                    key={item}
                    src={item}
                    alt="Válvula"
                    onClick={() => setZoomModal({ visible: true, images: imagesValvula, index: i, title: "Fotos da Válvula" })}
                  />
                ))}
              </div>
            </div>
          </div>
        </ConditionalField>

        <ConditionalField value={imagesPlaqueta.length > 0 ? imagesPlaqueta : null}>
          <div className="form-group">
            <label className="titulo-upload">Fotos Plaqueta do Fabricante:</label>
            <div className="images-container">
              <div className="images-display">
                {imagesPlaqueta.map((item, i) => (
                  <img
                    key={item}
                    src={item}
                    alt="Plaqueta"
                    onClick={() => setZoomModal({ visible: true, images: imagesPlaqueta, index: i, title: "Fotos da Plaqueta" })}
                  />
                ))}
              </div>
            </div>
          </div>
        </ConditionalField>
      </Form>

      <div
        style={{ display: "flex", justifyContent: "flex-end", marginTop: 24 }}
      >
        <Button
          className="voltarBotao"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Voltar
        </Button>
      </div>

      <ImageZoomModal
        visible={zoomModal.visible}
        images={zoomModal.images}
        currentIndex={zoomModal.index}
        title={zoomModal.title}
        onClose={() => setZoomModal((m) => ({ ...m, visible: false }))}
      />
    </>
  );
}

export default ValvulaControleForm;
